import { useEffect } from 'react'
import { type NextPage } from 'next'
import { useRouter } from 'next/router'

import { Loader } from '~/domains/platform/design-system/loader/loader'
import BlackBird from '~/domains/platform/lib/blackbird'
import analitica from '~/domains/stone-account/ports/analitica'
import { usePendingUserAssessments } from '~/domains/stone-account/ports/hooks/use-pending-user-assessments'
import { type KycRouteKind, resolveKycRoute } from '~/domains/stone-account/ports/kyc-assessment-routing'
import { resolveLegacyKycRoute } from '~/domains/stone-account/ports/kyc-assessment-routing-legacy'
import { kycRules } from '~/domains/stone-account/ports/kyc-rules'
import { CHALLENGE_BLOCKED_PTBR } from '~/domains/stone-account/ports/stone-account.routes'
import { trackEvents } from '~/lib/helpers'
import { CleanPageContainer } from '~/ui/containers'

import { type WithDeusExMachinaProps } from '../deus-ex-machina/ports'

type PageRest = Omit<WithDeusExMachinaProps, 'subject' | 'entity'>

interface KycGateProps extends WithDeusExMachinaProps {
  Page: NextPage<WithDeusExMachinaProps>
}

const loader = (
  <CleanPageContainer>
    <Loader />
  </CleanPageContainer>
)

function readKycPrefs() {
  const dontShowKycUpdateAssessment: string[] =
    window && JSON.parse(window.localStorage.getItem('dontShowKycUpdateAssessment') || '[]')

  const dontShowChallengeBlocked: string = (window && window.localStorage.getItem('dontShowChallengeBlocked')) || ''

  return { dontShowKycUpdateAssessment, dontShowChallengeBlocked }
}

function KycRouteOutlet({
  kind,
  hasNoAvailableIdentities,
  Page,
  subject,
  entity,
  rest
}: {
  kind: KycRouteKind
  hasNoAvailableIdentities: boolean
  Page: NextPage<WithDeusExMachinaProps>
  subject: WithDeusExMachinaProps['subject']
  entity: WithDeusExMachinaProps['entity']
  rest: PageRest
}) {
  const { push } = useRouter()

  switch (kind) {
    case 'challenge-blocked':
      push({ pathname: CHALLENGE_BLOCKED_PTBR })
      return loader
    case 'risk-blocked':
      trackEvents({ trackServices: ['analytics'], action: 'redirecting_user_to_blocked_page', category: 'KYC' })
      BlackBird.travelTo('/conta-bloqueada')
      return loader
    case 'analysis':
      trackEvents({ trackServices: ['analytics'], action: 'redirecting_user_to_kyc_analysis', category: 'KYC' })
      BlackBird.travelTo('/conta-em-analise')
      return loader
    case 'confirm-email':
      push('/confirme-email')
      return loader
    case 'select-account':
      trackEvents({ trackServices: ['analytics'], action: 'redirecting_user_to_account_switch', category: 'KYC' })
      window && window.sessionStorage.setItem('hasNoAvailableIdentities', JSON.stringify(hasNoAvailableIdentities))
      BlackBird.travelTo('/selecionar-conta')
      return loader
    default:
      return <Page subject={subject} entity={entity} {...rest} />
  }
}

function OneStoneKycGate({ Page, subject, entity, ...rest }: KycGateProps) {
  const { assessments, isLoading, isError } = usePendingUserAssessments()

  useEffect(() => {
    analitica.events.kycGate.flowEntered('one_stone')
  }, [])

  if (isLoading) return loader

  const { hasNoAvailableIdentities } = kycRules.kycIdentityRules({ entity, subject })

  const kind: KycRouteKind = isError
    ? 'render'
    : resolveKycRoute({
        assessments,
        subject,
        entity,
        hasNoAvailableIdentities,
        currentPathname: BlackBird.getPathname(),
        prefs: readKycPrefs()
      }).kind

  return (
    <KycRouteOutlet
      kind={kind}
      hasNoAvailableIdentities={hasNoAvailableIdentities}
      Page={Page}
      subject={subject}
      entity={entity}
      rest={rest}
    />
  )
}

/**
 * @deprecated Gate for legacy bff/entity sessions (deus-ex-machina getExMachinaSource).
 * Remove together with resolveLegacyKycRoute once every user is on the One Stone session.
 */
function LegacyKycGate({ Page, subject, entity, ...rest }: KycGateProps) {
  useEffect(() => {
    analitica.events.kycGate.flowEntered('legacy')
  }, [])

  const rules = kycRules.kycIdentityRules({ entity, subject })

  const { kind } = resolveLegacyKycRoute({
    subject,
    entity,
    rules,
    currentPathname: BlackBird.getPathname(),
    prefs: readKycPrefs()
  })

  return (
    <KycRouteOutlet
      kind={kind}
      hasNoAvailableIdentities={rules.hasNoAvailableIdentities}
      Page={Page}
      subject={subject}
      entity={entity}
      rest={rest}
    />
  )
}

export function withEntityKYC(Page: NextPage<WithDeusExMachinaProps>) {
  function WrappedPage({ subject, entity, ...rest }: WithDeusExMachinaProps) {
    if (entity.stoneAccount?.situation === 'one_stone') {
      return <OneStoneKycGate Page={Page} subject={subject} entity={entity} {...rest} />
    }

    return <LegacyKycGate Page={Page} subject={subject} entity={entity} {...rest} />
  }

  WrappedPage.displayName = `withEntityKYC(${Page.displayName})`

  return WrappedPage
}
